import { Archive, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { CategoryBadges } from './category-badges';
import { FirstShareMeta, SlackPermalink } from './first-share';
import { LinkFavicon, LinkThumbnail } from './link-media';
import { ProcessingStatusBadge } from '@/components/status-badge';
import { cn } from '@/lib/utils';
import type { LinkSummary } from '@/types/api';

/**
 * Compact list rendering of the same data as LinkCard. Same rule applies: all third-party
 * text is rendered as escaped JSX text nodes, never as HTML.
 */
export function LinkRow({
  link,
  categoryLabels,
}: {
  link: LinkSummary;
  categoryLabels?: Map<string, string>;
}) {
  const title = link.title?.trim() || link.domain;
  const showStatus = link.processingStatus !== 'COMPLETE';

  return (
    <article
      className={cn(
        'flex gap-4 rounded-lg border border-border bg-card p-3 text-card-foreground shadow-sm',
        'transition-shadow focus-within:shadow-md hover:shadow-md sm:p-4',
      )}
    >
      <Link
        to={`/links/${encodeURIComponent(link.linkId)}`}
        tabIndex={-1}
        aria-hidden="true"
        className="hidden h-20 w-32 shrink-0 overflow-hidden rounded-md border border-border bg-muted sm:block"
      >
        <LinkThumbnail link={link} />
      </Link>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
            <LinkFavicon link={link} />
            <span className="truncate">{link.domain}</span>
          </span>
          {link.archived ? (
            <Badge variant="outline" className="gap-1">
              <Archive aria-hidden="true" className="h-3 w-3" />
              Archived
            </Badge>
          ) : null}
          {showStatus ? <ProcessingStatusBadge status={link.processingStatus} /> : null}
        </div>

        <h3 className="text-sm font-semibold leading-snug">
          <Link
            to={`/links/${encodeURIComponent(link.linkId)}`}
            className={cn(
              'line-clamp-1 rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2',
              'focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            )}
          >
            {title}
          </Link>
        </h3>

        {link.description?.trim() ? (
          <p className="line-clamp-1 text-sm text-muted-foreground">{link.description}</p>
        ) : null}

        <CategoryBadges slugs={link.categories} labels={categoryLabels} max={4} />

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <FirstShareMeta share={link.firstShare} compact />
          <a
            href={link.originalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md text-xs font-medium text-primary',
              'hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            )}
          >
            <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
            Open original
            <span className="sr-only"> page for {title} (opens in a new tab)</span>
          </a>
          <SlackPermalink share={link.firstShare} label="First Slack message" />
        </div>
      </div>
    </article>
  );
}
